/**
 * @file OrderItemsTab.tsx
 * @description Вкладка «Позиции» заказа: состав и его правка менеджером
 * (phase10 → редактор phase19.15 шаг 4 → привязка позиции phase19.24 шаг 5).
 *
 * Править состав можно только до оплаты и до подтверждения клиентом (F30) —
 * условие считает `resolveOrderItemsEditability` (зеркало сервера). На
 * замороженном заказе кнопок правки нет вовсе, вместо них — объяснение,
 * почему: иначе менеджер жал бы кнопку и получал 422.
 *
 * Исключение — позиция без товара каталога (OQ-54): её привязка разрешена и
 * на стадии «На сборке», потому что иначе заказ стоит намертво. Поэтому
 * «Привязать товар» живёт отдельно от режима правки состава.
 *
 * После каждой правки список перечитываем с сервера (один источник правды), а
 * из ответа берём текст изменения для тоста, признак отзыва предложения и
 * признак «доставку надо пересчитать» (вес/сумма поехали — тариф мог устареть).
 */

import { useCallback, useEffect, useState } from "react";

import { OrderItemRelinkFlow } from "./OrderItemRelinkFlow";
import { OrderItemsList } from "./OrderItemsList";
import { OrderItemsToolbar } from "./OrderItemsToolbar";
import { SkuPickerDialog } from "./SkuPickerDialog";
import { useOrderTabRevisit } from "../../hooks/useOrderTabRevisit";
import {
  addOrderItem,
  listOrderItems,
  orderItemsErrorText,
  removeOrderItem,
  updateOrderItemQuantity,
  type OrderItemRawDto,
  type OrderItemsEditResultDto,
} from "../../lib/api-order-items";
import type { DeliveryAgreementDto } from "../../lib/api-delivery-agreement";
import type { OrderDto } from "../../lib/api-orders";
import {
  orderItemsEditContext,
  resolveOrderItemsEditability,
} from "../../lib/order-items-edit";
import { Alert } from "../ui/alert";
import { useNotify } from "../ui/NotifyProvider";
import { OrderTabSkeleton } from "./OrderTabSkeleton";

interface Props {
  order: OrderDto;
  /** Состояние заявки F30; `null` — обычный заказ. */
  agreement: DeliveryAgreementDto | null;
  canManage: boolean;
  /** id позиции из `?tab=items&focus=<id>`: строку обводим (deep-link). */
  focusId?: string | null;
  /** Перечитать карточку заказа (суммы в шапке) после правки состава. */
  onChanged: () => void | Promise<void>;
  /** Вкладка сейчас открыта: при возврате на неё список дочитывается тихо. */
  active?: boolean;
}

export function OrderItemsTab({
  order,
  agreement,
  canManage,
  focusId = null,
  onChanged,
  active = true,
}: Props) {
  const notify = useNotify();
  const [items, setItems] = useState<OrderItemRawDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Позиция, которой выбирают товар каталога; null — диалога нет. */
  const [relinkItem, setRelinkItem] = useState<OrderItemRawDto | null>(null);

  const editability = resolveOrderItemsEditability(
    orderItemsEditContext(order, agreement),
  );

  const load = useCallback(async (): Promise<void> => {
    try {
      setItems(await listOrderItems(order.id));
      setError(null);
    } catch (err) {
      setError(orderItemsErrorText(err, "Не удалось загрузить состав заказа"));
    }
  }, [order.id]);

  useEffect(() => {
    void load();
  }, [load]);
  useOrderTabRevisit(active, load);

  /** Общий путь любой правки: запрос → тосты → перечитать состав и заказ. */
  async function run(fn: () => Promise<OrderItemsEditResultDto>): Promise<void> {
    setBusy(true);
    try {
      const res = await fn();
      // Пустой changeSummary (сервер не описал правку) — тост без текста
      // выглядел бы сбоем: молча пропускаем, правка всё равно видна в списке.
      const summary = res.changeSummary?.trim() ?? "";
      if (summary) notify.success(summary);
      if (res.agreementReset) {
        notify.warning("Предложение отозвано — отправьте на подтверждение заново");
      }
      if (res.deliveryRecalcNeeded) {
        notify.warning(
          "Состав изменился — проверьте стоимость доставки (тариф мог устареть)",
        );
      }
      await load();
      await onChanged();
    } catch (err) {
      notify.error(orderItemsErrorText(err));
      // 422 «состав заморожен» означает, что заказ уехал вперёд, пока вкладка
      // была открыта: выходим из режима правки и перечитываем данные.
      await load();
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function addMany(skuIds: string[]): Promise<void> {
    setPickerOpen(false);
    for (const skuId of skuIds) {
      // Последовательно: сервер пересчитывает итог заказа на каждой правке,
      // параллельные запросы дали бы гонку по суммам и резервам.
      await run(() => addOrderItem(order.id, { skuId, quantity: 1 }));
    }
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (items === null) {
    return (
      // Заготовка высотой блока, а не спиннер: страница не прыгает (qa4 п. 14).
      <OrderTabSkeleton />
    );
  }

  const canEdit = canManage && editability.editable;

  return (
    <div className="flex flex-col gap-3">
      <OrderItemsToolbar
        canEdit={canEdit}
        editing={editing}
        busy={busy}
        lockMessage={editability.editable ? null : editability.message}
        onToggleEditing={() => setEditing((v) => !v)}
        onAdd={() => setPickerOpen(true)}
      />

      <OrderItemsList
        order={order}
        items={items}
        // Заказ мог уехать вперёд, пока вкладка была открыта (оплата,
        // подтверждение клиента): контролы гаснут вместе с правом.
        editing={editing && canEdit}
        busy={busy}
        canRelink={canManage}
        focusId={focusId}
        onQuantity={(item, quantity) =>
          void run(() => updateOrderItemQuantity(order.id, item.id, quantity))
        }
        onRemove={(item) => void run(() => removeOrderItem(order.id, item.id))}
        onRelink={(item) => setRelinkItem(item)}
      />

      {pickerOpen ? (
        <SkuPickerDialog
          onClose={() => setPickerOpen(false)}
          onSelectMany={(skus) => void addMany(skus.map((s) => s.id))}
        />
      ) : null}

      {relinkItem ? (
        <OrderItemRelinkFlow
          orderId={order.id}
          item={relinkItem}
          onClose={() => setRelinkItem(null)}
          onDone={() => {
            void load();
            void onChanged();
          }}
        />
      ) : null}
    </div>
  );
}
